import React from "react";
import MuiTooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";

/**
 * Tooltip component wrapper for contextual information.
 * Wraps Material-UI Tooltip with theme integration.
 * 
 * @component
 * @example
 * <Tooltip title="Delete">
 *   <IconButton>
 *     <DeleteIcon />
 *   </IconButton>
 * </Tooltip>
 */
const Tooltip = ({ children, ...props }) => {
  return <MuiTooltip {...props}>{children}</MuiTooltip>;
};

Tooltip.propTypes = {
  /**
   * Tooltip reference element
   */
  children: PropTypes.element.isRequired,
  /**
   * If true, adds an arrow to the tooltip
   */
  arrow: PropTypes.bool,
  /**
   * If true, the tooltip is shown
   */
  open: PropTypes.bool,
  /**
   * Tooltip placement
   */
  placement: PropTypes.oneOf([
    "bottom-end",
    "bottom-start",
    "bottom",
    "left-end",
    "left-start",
    "left",
    "right-end",
    "right-start",
    "right",
    "top-end",
    "top-start",
    "top",
  ]),
  /**
   * Tooltip title. Zero-length titles string are never displayed
   */
  title: PropTypes.node,
  /**
   * The number of milliseconds to wait before showing the tooltip
   */
  enterDelay: PropTypes.number,
  /**
   * The number of milliseconds to wait before hiding the tooltip
   */
  leaveDelay: PropTypes.number,
  /**
   * Callback fired when the component requests to be closed
   */
  onClose: PropTypes.func,
  /**
   * Callback fired when the component requests to be open
   */
  onOpen: PropTypes.func,
  /**
   * If true, the tooltip follow the cursor over the wrapped element
   */
  followCursor: PropTypes.bool,
  /**
   * Do not respond to focus-visible events
   */
  disableFocusListener: PropTypes.bool,
  /**
   * Do not respond to hover events
   */
  disableHoverListener: PropTypes.bool,
  /**
   * Do not respond to long press touch events
   */
  disableTouchListener: PropTypes.bool,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - maintain exact MUI Tooltip behavior

export default Tooltip;

